import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { Badge } from '@shadcn/components/ui/badge';

import {
  Search,
  
  Filter,
  BarChart3,
  Settings,
  
  AlertCircle,
  Clock,
  
} from 'lucide-react';
import { Link } from 'react-router-dom';


import { useTicketDashboard } from '../hooks/useTicketDashboard';
import { useDebouncedSearch } from '../hooks/useDebounce';

import { TicketTypeIcon } from '../components/TicketSystem/TicketTypeIcon';
import { ExportButton } from '../components/TicketSystem/ExportButton';
import {
  TICKET_TYPES,
  
  TICKET_TYPE_LABELS,
  
  
} from '../constants';

import TicketTable from '../components/Dashboard/TicketTable';
import TicketFilters from '../components/Dashboard/TicketFilters';
import DashboardStats from '../components/Dashboard/DashboardStats';
import TicketDetailModal from '../components/Dashboard/TicketDetailModal';
import QuickStatusFilters from '../components/Dashboard/QuickStatusFilters';


const TicketDashboardPage = () => {
  
  const [showFilters, setShowFilters] = useState(false);
  const [showStats, setShowStats] = useState(true); 
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeMetaStatus, setActiveMetaStatus] = useState('');

  
  const {
    tickets,
    statistics,
    filters,
    error,
    isLoading,
    updateFilters,
    resetFilters,
    goToPage,
    changePageSize,
    totalPages,
    totalItems,
    currentPage,
    hasNextPage,
    hasPrevPage
  } = useTicketDashboard(useMemo(() => ({}), []), false);

  
  const handleSearchChange = useCallback((term) => {
    updateFilters({ search: term, page: 1 });
  }, [updateFilters]);

  // Debounced search
  const {
    searchTerm,
    setSearchTerm,
    
    isSearching,
    clearSearch
  } = useDebouncedSearch(handleSearchChange);

  /**
   * Handle quick filter by meta status
   */
  const handleMetaStatusFilter = useCallback((metaStatus) => {
    setActiveMetaStatus(metaStatus);
    updateFilters({ 
      metaStatus: metaStatus === activeMetaStatus ? '' : metaStatus,
      page: 1 
    });
  }, [activeMetaStatus, updateFilters]);

  /**
   * Handle ticket type filter
   */
  const handleTicketTypeFilter = useCallback((ticketType) => {
    updateFilters({ 
      type: ticketType === filters.type ? '' : ticketType,
      page: 1 
    });
  }, [filters.type, updateFilters]);

  const handleViewTicket = useCallback((ticket) => {
    setSelectedTicket(ticket);
  }, []);


  const handleFiltersChange = useCallback((newFilters) => {
    updateFilters({ ...newFilters, page: 1 });
  }, [updateFilters]);


  const handleClearFilters = useCallback(() => {
    resetFilters();
    clearSearch();
    setActiveMetaStatus('');
  }, [resetFilters, clearSearch]);

  
  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (filters.type) count++;
    if (filters.metaStatus) count++;
    if (filters.search) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.open) count++;
    if (filters.customerName) count++;
    if (filters.storeName) count++;
    if (filters.orderNumber) count++;
    if (filters.staffName) count++;
    return count;
  }, [filters]);

  return (  
    <div className="page-content">
    
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Ticket Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Unified view of all tickets from orders, repairs, and warranty claims.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStats(!showStats)}
              className="hidden md:inline-flex"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {showStats ? 'Hide' : 'Show'} Stats
            </Button>

            <div className="flex gap-2 sm:contents">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(true)}
                className="flex-1 sm:flex-none items-center px-4 justify-center"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {getActiveFiltersCount()}
                  </Badge>
                )}
              </Button>

              <Button variant="outline" size="sm">
             <Settings className="w-4 h-4" />  
                <Link to="/ticket-dashboard/status-mappings"
                  className="flex-1 flex-row items-center px-2"
                >
            
                  Status Mappings
                </Link>
                </Button>

              {/* <ExportButton
                filters={filters}
                filename="tickets-dashboard"
                size="sm"
                className="flex-1 sm:flex-none items-center px-4"
              /> */}
             
           
            </div>
          </div>
        </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics Panel */}
      {showStats && statistics && (
        <DashboardStats 
          statistics={statistics}
          onMetaStatusClick={handleMetaStatusFilter}
          activeMetaStatus={activeMetaStatus}
        />
      )}

      {/* Filters Panel */}
      {showFilters && (
        <TicketFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onClose={() => setShowFilters(false)}
          open={showFilters}
        />
      )}

        {/* Search and Quick Filters */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by customer name, order number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Clock className="w-4 h-4 text-gray-400 animate-pulse" />
                    </div>
                  )}
                </div>

                {searchTerm && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSearch}
                    className="w-full sm:w-auto"
                  >
                    Clear Search
                  </Button>
                )}
              </div>

            {/* Quick Status Filters */}
            <QuickStatusFilters
              statistics={statistics}
              activeMetaStatus={activeMetaStatus}
              onStatusClick={handleMetaStatusFilter}
            />

              {/* Ticket Type Filters */}
              <div className="space-y-3">
                <span className="text-sm font-medium text-gray-700 hidden sm:block">Filter by type:</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={!filters.type ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleTicketTypeFilter('')}
                    className="flex-1 sm:flex-none"
                  >
                    All Types
                  </Button>

                  {Object.values(TICKET_TYPES).map(type => (
                    <Button
                      key={type}
                      variant={filters.type === type ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTicketTypeFilter(type)}
                      className="flex items-center gap-2 flex-1 sm:flex-none"
                    >
                      <TicketTypeIcon type={type} size="xs" showBackground={false} />
                      <span className="hidden sm:inline">{TICKET_TYPE_LABELS[type]}</span>
                      <span className="sm:hidden">{TICKET_TYPE_LABELS[type].slice(0, 3)}</span>
                    </Button>
                  ))}
                </div>
              </div>
          </div>
        </CardContent>
      </Card>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <CardTitle>Tickets</CardTitle>
                <CardDescription>
                  {totalItems > 0 ? (
                    <>
                      <span className="sm:hidden">{totalItems} tickets</span>
                      <span className="hidden sm:inline">
                        Showing {((currentPage - 1) * filters.limit) + 1} to{' '}
                        {Math.min(currentPage * filters.limit, totalItems)} of {totalItems} tickets
                      </span>
                    </>
                  ) : (
                    'No tickets found'
                  )}
                </CardDescription>
              </div>

              <div className="hidden sm:flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Per page:</span>
                <select
                  value={filters.limit}
                  onChange={(e) => changePageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </CardHeader>
        
        <CardContent>
          <TicketTable
            tickets={tickets?.items || []}
            loading={isLoading}
            onViewTicket={handleViewTicket}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPrevPage}
          />
          </CardContent>
        </Card>

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <TicketDetailModal
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
          />
        )}
      </div>
    </div>
    </div>
  );
};

export default TicketDashboardPage;
